import './dispatchMobile.css';
import ResponsiveRow from '../shared/ResponsiveRow';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import ModalSurface from '../shared/ModalSurface';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TestChip from '../breaker/TestChip';
import { useModuleMode } from '../../hooks/useModuleModes';
import { displayNumber, filterTestRows, showTestRecords } from '../../lib/breakerPanel';
import { supabaseClient } from '../../config/supabase';
import AssignmentDetailCard from './AssignmentDetailCard';
import EscalatedIntakeDetailCard from './EscalatedIntakeDetailCard';
import { deleteTestBookings } from '../../lib/businessSchedule';
import { assignmentStatusMeta, WATERFALL_PHASES } from './dispatchConstants';
import { requestAffiliateTripLink, shouldAutoMintAffiliateTripLink } from '../../lib/affiliateTripLinks';
import HoverHelp from '../shared/HoverHelp';
import { formatPhone } from '../../lib/phone';
import ManualDriverActivation from './ManualDriverActivation';
import useModuleAccess from '../../hooks/useModuleAccess';
import ReservationTile from '../communications/ReservationTile';
import { approveReservation, loadReservationForTrip } from '../../lib/reservationApi';
import { excludeEventRecords } from '../../lib/eventEngagement';
import TripChangeReview from './TripChangeReview';
import FlightSuggestionCard from './FlightSuggestionCard';
import { loadFlightDetails } from '../../lib/tripFlight';
import StaffReservation from '../reservations/StaffReservation';

function truncatedId(id) {
    return id ? `${id.slice(0, 8)}…` : '—';
}

function driverPrimaryLabel(driver, fallbackId = null) {
    const profileName = driver?.user_profile?.full_name?.trim();
    return profileName || truncatedId(driver?.id || fallbackId);
}

// Module 11 — Dispatcher board (was "Dispatch Board"; renamed in the 2026-10-25 UX pass) (Gate 1: Admin Dispatch UI, BL-168).
//
// Four tabs in one view:
//   1. Dispatch Queue  — confirmed engagements awaiting assignment, waterfall view
//   2. Assignment History — all assignments with inline detail (BL-183: confirmation_number visible)
//   3. Payable Staff — internal (w2_staff) driver roster, Dispatch-full tier
//   4. Escalated Intake — AI Attendant handoffs awaiting staff resolution
//
// Affiliate roster lives in DriversView (filtered to 1099_affiliate).
// Four-tier field visibility: this file renders Dispatch-full only.
function DispatchBoardView({ userId, theme = 'light', isUser = false, embedded = false }) {
    const dark = theme === 'dark';
    const { allowed: dispatchEditAllowed } = useModuleAccess(isUser ? 'dispatch' : null, 'edit');
    const canEditDispatch = !isUser || dispatchEditAllowed;

    // ---- State ---------------------------------------------------------------
    const [tab, setTab] = useState('queue');

    // Engagements confirmed/scheduled (eligible for dispatch)
    const [queueEngagements, setQueueEngagements] = useState([]);
    const [queueLoading, setQueueLoading] = useState(true);
    const [queueError, setQueueError] = useState(null);
    // FA-2: engagement id -> flight record (flight line + "suggested pickup · Apply" on the trip card)
    const [flightsByEngagement, setFlightsByEngagement] = useState({});

    // All assignments
    const [assignments, setAssignments] = useState([]);
    const [assignLoading, setAssignLoading] = useState(true);
    const [assignError, setAssignError] = useState(null);
    const [liveTrackingByAssignment, setLiveTrackingByAssignment] = useState({});

    // Drivers + vehicles (for display only; assignment write is in the form modal below)
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [driverLoadError, setDriverLoadError] = useState(null);

    // Inline detail
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [escalations, setEscalations] = useState([]);
    const [escalationLoading, setEscalationLoading] = useState(true);
    const [escalationError, setEscalationError] = useState(null);
    const [selectedEscalation, setSelectedEscalation] = useState(null);
    const [resolvingEscalation, setResolvingEscalation] = useState(false);
    const [accounts, setAccounts] = useState([]);

    // Assignment create form
    const [offerTarget, setOfferTarget] = useState(null); // engagement row to assign
    const [offerDriverId, setOfferDriverId] = useState('');
    const [offerType, setOfferType] = useState('internal');
    const [offerSubmitting, setOfferSubmitting] = useState(false);
    const [offerError, setOfferError] = useState(null);
    const [offerNotice, setOfferNotice] = useState(null);

    // BL-177 Step 2k: a trip made from a conversation with details missing is completed here, in the same tile.
    const [completing, setCompleting] = useState(null); // { reservation }
    const [completeBusy, setCompleteBusy] = useState(false);
    const [completeError, setCompleteError] = useState('');
    const focusReservationId = useMemo(() => {
        try { return new URLSearchParams(window.location.search).get('reservation') || ''; } catch { return ''; }
    }, []);

    // History search/filter
    const [historySearch, setHistorySearch] = useState('');
    const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

    // Breaker Panel G4: the Show-TEST filter (on while Dispatch is in Test, off once Live) and the visible queue.
    const dispatchMode = useModuleMode('dispatch');
    const [showTestToggle, setShowTestToggle] = useState(null);
    const showTest = showTestRecords(dispatchMode, showTestToggle);
    const visibleQueue = useMemo(() => filterTestRows(queueEngagements, { mode: dispatchMode, toggle: showTestToggle }), [queueEngagements, dispatchMode, showTestToggle]);

    // ---- Data loading --------------------------------------------------------
    const loadQueue = useCallback(async () => {
        setQueueError(null);
        // BL-177 Step 2k: the pickup time lives on the linked schedule row (engagements has no scheduled_at column, so the old select
        // failed); needs_info / needs_info_fields drive the "Needs info" tag on trips made from a conversation.
        // BL-177 Step 2m / Breaker Panel: is_test marks TEST bookings (tagged TEST below). They may be assigned, billed and notified only
        // within the company test list (the database enforces it); test_contact_address marks the AI Attendant's own test bookings.
        // If the columns are not there yet (this page can go live a moment before the database change), load the queue without them.
        const columns = 'id, confirmation_number, status, service_type, pickup_address, dropoff_address, origin, destination, entity_id, created_at, needs_info, needs_info_fields, event_engagement_details(engagement_id), schedule:schedule!engagements_schedule_id_fkey(scheduled_start)';
        const queueQuery = (select) => supabaseClient.from('engagements').select(select).in('status', ['confirmed', 'scheduled', 'in_progress']);
        let { data, error } = await queueQuery(`${columns}, is_test, test_contact_address`);
        if (error && /is_test|test_contact_address/.test(error.message || '')) ({ data, error } = await queueQuery(columns));
        if (error) { setQueueError(error.message); setQueueEngagements([]); }
        else {
            const startOf = (row) => { const value = row.schedule?.scheduled_start; return value ? Date.parse(value) : Infinity; };
            const rows = excludeEventRecords(data).sort((a, b) => startOf(a) - startOf(b));
            setQueueEngagements(rows);
            setFlightsByEngagement(await loadFlightDetails(supabaseClient, rows.map((row) => row.id)));
        }
        setQueueLoading(false);
    }, []);

    // BL-177 Step 2m: a TEST booking is deleted in one click (admins; the database refuses anyone else and anything that is not a TEST booking).
    const deleteTestBooking = useCallback(async (engagement) => {
        setQueueError(null);
        try {
            await deleteTestBookings(supabaseClient, null, engagement.id);
            await loadQueue();
        } catch (failure) {
            setQueueError(failure?.message || 'The test booking could not be deleted.');
        }
    }, [loadQueue]);

    const completeTrip = useCallback(async (engagement) => {
        setCompleteError('');
        try {
            const response = await loadReservationForTrip(supabaseClient, engagement.id);
            setCompleting({ reservation: response.reservation });
        } catch (failure) {
            setQueueError(failure.message || 'Could not open this reservation.');
        }
    }, []);

    const approveCompletion = useCallback(async (fields) => {
        if (!completing) return;
        setCompleteBusy(true);
        setCompleteError('');
        try {
            const response = await approveReservation(supabaseClient, completing.reservation.id, fields);
            if (response.stillIncomplete) {
                setCompleting({ reservation: response.reservation });
                setCompleteError('Saved. Some details are still missing, so the client confirmation stays on hold.');
            } else {
                setCompleting(null);
            }
            loadQueue();
        } catch (failure) {
            setCompleteError(failure.message || 'The reservation could not be saved.');
        } finally {
            setCompleteBusy(false);
        }
    }, [completing, loadQueue]);

    const loadAssignments = useCallback(async () => {
        setAssignError(null);
        const { data, error } = await supabaseClient
            .from('assignments')
            .select('*')
            .order('created_at', { ascending: false });
        if (error) { setAssignError(error.message); setAssignments([]); }
        else setAssignments(data || []);
        setAssignLoading(false);
    }, []);

    const loadEscalations = useCallback(async () => {
        setEscalationError(null);
        try {
            const [sessionsResult, accountsResult] = await Promise.all([
                supabaseClient
                    .from('intake_sessions')
                    .select('id, channel, account_id, contact_phone, contact_email, client_tier, escalation_reason, itinerary_short_form, pass_count, pass_cap, last_inbound_at, created_at, capture_checklist, conversation_log, raw_message_log')
                    .eq('outcome', 'escalated'),
                supabaseClient.from('accounts').select('id, account_name').order('account_name'),
            ]);
            if (sessionsResult.error) {
                setEscalationError(sessionsResult.error.message);
                setEscalations([]);
            } else {
                setEscalations((sessionsResult.data || []).sort((a, b) =>
                    new Date(a.last_inbound_at || a.created_at) - new Date(b.last_inbound_at || b.created_at)
                ));
            }
            if (!accountsResult.error) setAccounts(accountsResult.data || []);
        } catch (error) {
            setEscalationError(error.message || 'Unable to load escalated intake.');
            setEscalations([]);
        } finally {
            setEscalationLoading(false);
        }
    }, []);

    const loadDriversAndVehicles = useCallback(async () => {
        const dRes = await supabaseClient.rpc('bl395a_dispatch_driver_directory');
        if (dRes.error) {
            setDriverLoadError(dRes.error.message);
            setDrivers([]);
            setVehicles([]);
        } else {
            setDriverLoadError(null);
            const loadedDrivers = (dRes.data || []).map((driver) => ({
                ...driver,
                id: driver.driver_id,
                user_profile: { full_name: driver.display_name },
            }));
            setDrivers(loadedDrivers);
            setVehicles(loadedDrivers.filter((driver) => driver.default_vehicle_id).map((driver) => ({
                id: driver.default_vehicle_id,
                driver_id: driver.id,
                label: driver.vehicle_label,
                plate: driver.vehicle_plate,
                vehicle_class: driver.vehicle_class,
                capacity: driver.vehicle_capacity,
            })));
        }
    }, []);

    const loadLiveTracking = useCallback(async () => {
        const { data, error } = await supabaseClient
            .from('assignment_live_tracking')
            .select('assignment_id, latitude, longitude, eta_seconds, updated_at');
        if (error) return;
        const next = {};
        (data || []).forEach((row) => {
            next[row.assignment_id] = row;
        });
        setLiveTrackingByAssignment(next);
    }, []);

    useEffect(() => {
        loadQueue();
        loadAssignments();
        loadEscalations();
        loadDriversAndVehicles();
        loadLiveTracking();

        const intervalId = setInterval(() => {
            loadLiveTracking();
            loadEscalations();
        }, 30000);

        return () => clearInterval(intervalId);
    }, [loadQueue, loadAssignments, loadEscalations, loadDriversAndVehicles, loadLiveTracking, userId]);

    // ---- Mutations -----------------------------------------------------------
    const handleOfferSubmit = async () => {
        if (!offerTarget || !offerDriverId) return;
        setOfferSubmitting(true);
        setOfferError(null);
        setOfferNotice(null);

        // Determine next attempt_sequence for this confirmation_number
        const existingAttempts = assignments.filter(a => a.confirmation_number === offerTarget.confirmation_number);
        const nextAttempt = existingAttempts.length + 1;

        const driver = drivers.find(d => d.id === offerDriverId);
        if (!driver || driver.status !== 'active') {
            setOfferError('Choose an active driver. Drivers still onboarding cannot receive assignments.');
            setOfferSubmitting(false);
            return;
        }
        const assignmentType = offerType || (driver?.employment_classification === '1099_affiliate' ? 'affiliate' : 'internal');
        const shouldMintAffiliateLink = shouldAutoMintAffiliateTripLink({ assignmentType, driver });

        const payload = {
            org_id: offerTarget.org_id || undefined, // RLS supplies org_id via auth_org_id() if omitted; explicit when available
            entity_id: offerTarget.entity_id,
            confirmation_number: offerTarget.confirmation_number,
            attempt_sequence: nextAttempt,
            assignment_type: assignmentType,
            status: 'offered',
            driver_id: shouldMintAffiliateLink ? null : offerDriverId,
            affiliate_id: shouldMintAffiliateLink ? offerDriverId : null,
            vehicle_id: driver?.default_vehicle_id || null,
            created_by: userId,
        };

        const { data: createdAssignment, error } = await supabaseClient
            .from('assignments')
            .insert([payload])
            .select('id')
            .single();
        if (error) {
            setOfferError(error.message);
        } else {
            if (shouldMintAffiliateLink && createdAssignment?.id) {
                try {
                    await requestAffiliateTripLink(createdAssignment.id, 'POST');
                    setOfferNotice('Affiliate status link minted and queued through the existing driver notification path.');
                } catch (linkError) {
                    setOfferNotice(`Assignment created, but the affiliate status link could not be minted automatically: ${linkError.message || 'affiliate_link_request_failed'}`);
                }
            }
            setOfferTarget(null);
            setOfferDriverId('');
            await loadAssignments();
            await loadQueue();
        }
        setOfferSubmitting(false);
    };

    const handleStatusChange = async (assignmentId, newStatus) => {
        const { error } = await supabaseClient
            .from('assignments')
            .update({ status: newStatus, updated_by: userId })
            .eq('id', assignmentId);
        if (!error) {
            await loadAssignments();
            // Refresh selected if it's this assignment
            if (selectedAssignment?.id === assignmentId) {
                const fresh = assignments.find(a => a.id === assignmentId);
                if (fresh) setSelectedAssignment({ ...fresh, status: newStatus });
            }
        }
    };

    const handleEscalationResolve = async (intakeSessionId) => {
        setResolvingEscalation(true);
        setEscalationError(null);
        const { error } = await supabaseClient
            .from('intake_sessions')
            .update({ outcome: 'resolved' })
            .eq('id', intakeSessionId);
        if (error) {
            setEscalationError(error.message);
        } else {
            setSelectedEscalation(null);
            await loadEscalations();
        }
        setResolvingEscalation(false);
    };

    // ---- Filtering -----------------------------------------------------------
    const filteredHistory = useMemo(() => {
        const q = historySearch.trim().toLowerCase();
        return assignments.filter(a => {
            if (historyStatusFilter !== 'all' && a.status !== historyStatusFilter) return false;
            if (!q) return true;
            return [a.confirmation_number, a.status, a.assignment_type, a.driver_id, a.affiliate_id, a.external_reference]
                .some(v => (v || '').toLowerCase().includes(q));
        });
    }, [assignments, historyStatusFilter, historySearch]);

    const accountsById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
    const driversById = useMemo(() => new Map(drivers.map((driver) => [driver.id, driver])), [drivers]);

    // ---- Styles --------------------------------------------------------------
    const cardCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const headCls = dark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200';
    const rowCls = dark ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-100 hover:bg-[var(--accent-ink)]/10';
    const controlCls = 'rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--ink)]';
    const tabBase = `px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors`;
    const tabActive = `border-[var(--accent-ink)] text-[var(--accent-ink)]`;
    const tabInactive = dark ? `border-transparent text-gray-400 hover:text-gray-200` : `border-transparent text-gray-600 hover:text-gray-800`;

    const StatusPill = ({ status }) => {
        const m = assignmentStatusMeta(status);
        return (
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${m.pill}`}>
                {m.icon} {m.label}
            </span>
        );
    };

    // Active assignments per confirmation_number (for queue highlighting)
    const assignedConfNums = useMemo(() => {
        const active = new Set(
            assignments
                .filter(a => !['declined', 'timed_out', 'completed', 'reassigned'].includes(a.status))
                .map(a => a.confirmation_number)
        );
        return active;
    }, [assignments]);

    const activeAssignmentsByConfirmation = useMemo(() => {
        const map = {};
        assignments
            .filter(a => !['declined', 'timed_out', 'completed', 'reassigned'].includes(a.status))
            .forEach((assignment) => {
                map[assignment.confirmation_number] = assignment;
            });
        return map;
    }, [assignments]);

    const formatEta = (seconds) => {
        const numeric = Number(seconds);
        if (!Number.isFinite(numeric) || numeric <= 0) return 'ETA unavailable';
        return `ETA ${Math.max(1, Math.round(numeric / 60))} min`;
    };

    // ---- Render --------------------------------------------------------------
    return (
        <div className="ia-dispatch h-full flex flex-col">
            {canEditDispatch && <StaffReservation client={supabaseClient} accounts={accounts} onChanged={loadQueue} />}
            {/* Header (UX pass: inside Dispatcher the page heading comes from DispatcherView) */}
            {!embedded && <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h1 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>🚐 Dispatcher</h1>
                    <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Waterfall queue, assignment history, and payable staff roster.
                    </p>
                </div>
            </div>}

            {/* Tab bar */}
            <ResponsiveRow className={`flex gap-1 border-b mb-4 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                <button className={`${tabBase} ${tab === 'queue' ? tabActive : tabInactive}`} onClick={() => setTab('queue')}>
                    📋 Dispatch Queue
                </button>
                <button className={`${tabBase} ${tab === 'history' ? tabActive : tabInactive}`} onClick={() => setTab('history')}>
                    📜 Assignment History
                </button>
                {!isUser && <button className={`${tabBase} ${tab === 'staff' ? tabActive : tabInactive}`} onClick={() => setTab('staff')}>
                    👷 Payable Staff
                </button>}
                <button className={`${tabBase} ${tab === 'escalated' ? tabActive : tabInactive}`} onClick={() => setTab('escalated')}>
                    🚨 Escalated Intake ({escalations.length})
                </button>
                <HoverHelp helpId="dispatch.queue-tabs" userId={userId} />
            </ResponsiveRow>

            {/* ================================================================
                TAB 1: DISPATCH QUEUE
                ================================================================ */}
            {tab === 'queue' && (
                <div className="flex-1 overflow-auto">
                    <TripChangeReview canEdit={canEditDispatch} onChanged={loadQueue} />
                    {/* Waterfall legend */}
                    {offerNotice && (
                        <div className={`mb-4 rounded-lg border px-3 py-2 text-sm ${dark ? 'border-lime-400/40 bg-lime-950/30 text-lime-100' : 'border-lime-200 bg-lime-50 text-lime-800'}`}>
                            {offerNotice}
                        </div>
                    )}
                    <ResponsiveRow className="flex flex-wrap items-center gap-3 mb-4">
                        {WATERFALL_PHASES.map(phase => (
                            <div key={phase.key}
                                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${dark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                                {phase.icon} {phase.label}
                            </div>
                        ))}
                        <HoverHelp helpId="dispatch.waterfall-legend" userId={userId} />
                        {/* Breaker Panel G4: TEST bookings show while Dispatch is in Test, hide once it is Live; the toggle overrides. */}
                        <button type="button" className="ia-hollow-pill" aria-pressed={showTest} onClick={() => setShowTestToggle(!showTest)} data-testid="show-test-toggle">
                            Show TEST
                        </button>
                        {canEditDispatch && <HoverHelp helpId="dispatch.assign-driver" userId={userId} />}
                    </ResponsiveRow>

                    {queueLoading ? (
                        <div className="p-10 text-center text-gray-500">Loading queue…</div>
                    ) : queueError ? (
                        <div className="p-6 text-center text-red-600">Could not load queue: {queueError}</div>
                    ) : visibleQueue.length === 0 ? (
                        <div className="p-10 text-center">
                            <div className="text-4xl mb-2">📋</div>
                            <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No engagements in queue</div>
                            <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Confirmed engagements awaiting assignment will appear here.</div>
                        </div>
                    ) : (
                        <div className={`rounded-xl border overflow-auto ${cardCls}`}>
                            <ResponsiveTable className="w-full text-sm">
                                <thead>
                                    <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                        <th className="px-4 py-3">Confirmation #</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Service</th>
                                        <th className="px-4 py-3">Route</th>
                                        <th className="px-4 py-3">Scheduled</th>
                                        <th className="px-4 py-3">Live GPS / ETA</th>
                                        <th className="px-4 py-3">Assignment</th>
                                        <th className="px-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleQueue.map(eng => {
                                        const hasActive = assignedConfNums.has(eng.confirmation_number);
                                        const activeAssignment = activeAssignmentsByConfirmation[eng.confirmation_number];
                                        const tracking = activeAssignment ? liveTrackingByAssignment[activeAssignment.id] : null;
                                        return (
                                            <tr key={eng.id} className={`border-b ${rowCls}`} data-highlight={eng.id === focusReservationId ? 'true' : undefined} style={eng.id === focusReservationId ? { outline: '2px solid #FB923C', outlineOffset: '-2px' } : undefined}>
                                                {/* BL-183: confirmation_number is the visible join key */}
                                                <ResponsiveCell label="Confirmation #" className={`px-4 py-3 font-mono font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                                                    {displayNumber(eng.confirmation_number, eng.is_test) || <span className="italic text-gray-400 font-sans font-normal">unconfirmed</span>}
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Status" className="px-4 py-3">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${dark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>
                                                        {eng.status}
                                                    </span>
                                                    {eng.is_test && (
                                                        <div className="mt-1" data-testid="test-booking-tag">
                                                            <TestChip />
                                                            <p className="mt-1 text-xs font-semibold text-[var(--ink)]">TEST booking (made while in Test). Only drivers, clients and contacts on the company test list get jobs, bills or notices.</p>
                                                            {/* Fix round 1 (finding 2): the one-click delete is only for the AI Attendant's test bookings
                                                                (admins, as 216). Every other TEST record is deleted Owner-only from the Go-Live Panel. */}
                                                            {!isUser && eng.test_contact_address ? (
                                                                <button type="button" onClick={() => deleteTestBooking(eng)} className="ia-btn3d ia-btn3d--pill mt-1">
                                                                    Delete test booking
                                                                </button>
                                                            ) : !isUser ? (
                                                                <p className="mt-1 text-xs text-[var(--ink-muted)]" data-testid="test-delete-hint">The Owner deletes TEST records in Admin Center › Go-Live Panel.</p>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                    {eng.needs_info && (
                                                        <div className="mt-1" data-testid="needs-info-tag">
                                                            <span className="ia-resv-tag">Needs info</span>
                                                            <p className="mt-1 text-xs font-semibold text-[var(--ink)]" data-testid="needs-info-fields">
                                                                {(Array.isArray(eng.needs_info_fields) ? eng.needs_info_fields : []).map((item) => item.label).join(', ') || 'Details missing'}
                                                            </p>
                                                            {canEditDispatch && (
                                                                <button type="button" onClick={() => completeTrip(eng)} className="ia-btn3d ia-btn3d--pill mt-1">
                                                                    Complete details
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Service" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{eng.service_type || '—'}</ResponsiveCell>
                                                <ResponsiveCell label="Route" className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {(eng.pickup_address || eng.origin || '—')} <span className="text-gray-400">→</span> {(eng.dropoff_address || eng.destination || '—')}
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Scheduled" className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                    {(eng.schedule?.scheduled_start || eng.scheduled_at) ? new Date(eng.schedule?.scheduled_start || eng.scheduled_at).toLocaleString() : '—'}
                                                    <FlightSuggestionCard detail={flightsByEngagement[eng.id]} client={supabaseClient} canEdit={canEditDispatch && !eng.is_test} onChanged={loadQueue} />
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Live GPS / ETA" className={`px-4 py-3 text-xs ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                    {tracking
                                                        ? (
                                                            <>
                                                                <div>{tracking.latitude}, {tracking.longitude}</div>
                                                                <div className="text-[11px] opacity-80">{formatEta(tracking.eta_seconds)}</div>
                                                            </>
                                                        )
                                                        : 'No live feed'}
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Assignment" className="px-4 py-3">
                                                    {hasActive
                                                        ? <span className="text-xs text-green-600 font-medium">✅ Active<span className="ia-dispatch-phone-driver">{driverPrimaryLabel(driversById.get(activeAssignment.driver_id || activeAssignment.affiliate_id), activeAssignment.driver_id || activeAssignment.affiliate_id)}</span></span>
                                                        : <span className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Unassigned</span>}
                                                </ResponsiveCell>
                                                <ResponsiveCell label="Action" className="px-4 py-3">
                                                    {/* Fix round 1 (finding 1): a TEST trip may be assigned; the database accepts only drivers on the company test list. */}
                                                    {canEditDispatch && eng.confirmation_number && !hasActive && (
                                                        <span className="inline-flex items-center">
                                                            <button
                                                                onClick={() => { setOfferTarget(eng); setOfferDriverId(''); setOfferError(null); setOfferNotice(null); }}
                                                                className="px-3 py-1 text-xs font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)]"
                                                            >
                                                                ➕ Assign
                                                            </button>
                                                        </span>
                                                    )}
                                                </ResponsiveCell>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </ResponsiveTable>
                        </div>
                    )}

                    {completing && (
                        <ReservationTile
                            key={completing.reservation.id}
                            reservation={completing.reservation}
                            countdown={false}
                            startInEdit
                            busy={completeBusy}
                            error={completeError}
                            title="Complete the reservation details"
                            subtitle="The client confirmation is on hold until every required detail is filled in."
                            approveLabel="Save and confirm"
                            onApprove={approveCompletion}
                            onCancel={() => setCompleting(null)}
                        />
                    )}

                    {/* Offer modal (inline form) */}
                    {offerTarget && canEditDispatch && (
                        <ModalSurface label="Assign Driver" onClose={() => { setOfferTarget(null); setOfferError(null); }} overlayClassName="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" panelClassName={`rounded-2xl border p-6 w-full max-w-md shadow-2xl ${cardCls}`}>
                            <div className="ia-modal-layout">
                            <div className="ia-modal-body">
                                <h3 className={`text-lg font-semibold mb-1 ${'text-[var(--ink)]'}`}>Assign Driver</h3>
                                {/* BL-183: visible confirmation_number */}
                                <p className={`text-sm mb-4 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Engagement <span className="font-mono font-semibold">{offerTarget.confirmation_number}</span>
                                    {offerTarget.service_type ? ` · ${offerTarget.service_type}` : ''}
                                </p>

                                <div className="space-y-3">
                                    {driverLoadError && <p role="alert" className="text-sm text-red-600">Could not load dispatch drivers: {driverLoadError}</p>}
                                    <div>
                                        <div className={`mb-1 flex items-center gap-1 text-xs font-medium ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            <span>Assignment Type</span>
                                            <HoverHelp helpId="dispatch.assignment-type" userId={userId} />
                                        </div>
                                        <select value={offerType} onChange={e => setOfferType(e.target.value)} className={`w-full ${controlCls}`}>
                                            <option value="internal">Internal (W-2 Staff)</option>
                                            <option value="affiliate">Affiliate (1099)</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className={`block text-xs font-medium mb-1 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Driver</label>
                                        <select value={offerDriverId} onChange={e => setOfferDriverId(e.target.value)} className={`w-full ${controlCls}`} disabled={Boolean(driverLoadError)}>
                                            <option value="">Select driver…</option>
                                            {drivers
                                                .filter(d => d.status === 'active')
                                                .filter(d => offerType === 'affiliate'
                                                    ? d.employment_classification === '1099_affiliate'
                                                    : d.employment_classification === 'w2_staff')
                                                .map(d => (
                                                    <option key={d.id} value={d.id}>
                                                        {driverPrimaryLabel(d)} ({d.employment_classification})
                                                    </option>
                                                ))}
                                        </select>
                                    </div>
                                </div>

                                {offerError && <p className="mt-3 text-sm text-red-600">{offerError}</p>}

                                </div>
                                <div className="ia-modal-actions flex justify-end gap-2 mt-5">
                                    <button onClick={() => { setOfferTarget(null); setOfferError(null); }}
                                        className={`px-4 py-2 text-sm rounded-lg border ${dark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleOfferSubmit}
                                        disabled={!offerDriverId || offerSubmitting}
                                        className="px-4 py-2 text-sm font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
                                    >
                                        {offerSubmitting ? 'Submitting…' : '📤 Offer Assignment'}
                                    </button>
                                </div>
                            </div>
                        </ModalSurface>
                    )}
                </div>
            )}

            {/* ================================================================
                TAB 2: ASSIGNMENT HISTORY
                ================================================================ */}
            {tab === 'history' && (
                <div className="flex-1 flex flex-col overflow-hidden">
                    <ResponsiveRow className="flex flex-wrap items-center gap-3 mb-4">
                        <input
                            type="text"
                            value={historySearch}
                            onChange={e => setHistorySearch(e.target.value)}
                            placeholder="Search confirmation #, status, type…"
                            className={`${controlCls} flex-1 min-w-[200px]`}
                        />
                        <select value={historyStatusFilter} onChange={e => setHistoryStatusFilter(e.target.value)} className={controlCls}>
                            <option value="all">All statuses</option>
                            {['offered','accepted','en_route','on_location','passenger_onboard','completed','declined','timed_out','reassigned'].map(s => (
                                <option key={s} value={s}>{assignmentStatusMeta(s).label}</option>
                            ))}
                        </select>
                        <HoverHelp helpId="dispatch.assignment-history-filter" userId={userId} />
                        <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{filteredHistory.length} shown</span>
                    </ResponsiveRow>

                    <div className={`flex-1 overflow-auto rounded-xl border ${cardCls}`}>
                        {assignLoading ? (
                            <div className="p-10 text-center text-gray-500">Loading assignments…</div>
                        ) : assignError ? (
                            <div className="p-6 text-center text-red-600">Could not load assignments: {assignError}</div>
                        ) : filteredHistory.length === 0 ? (
                            <div className="p-10 text-center">
                                <div className="text-4xl mb-2">📜</div>
                                <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No assignments yet</div>
                                <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Assignments created from the Dispatch Queue will appear here.</div>
                            </div>
                        ) : (
                            <ResponsiveTable className="w-full text-sm">
                                <thead>
                                    <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                        <th className="px-4 py-3">Confirmation #</th>
                                        <th className="px-4 py-3">Attempt</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3">Type</th>
                                        <th className="px-4 py-3">Driver</th>
                                        <th className="px-4 py-3">Created</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredHistory.map(a => {
                                        const assignedDriverId = a.driver_id || a.affiliate_id;
                                        const assignedDriver = assignedDriverId ? driversById.get(assignedDriverId) : null;
                                        return (
                                            <React.Fragment key={a.id}>
                                                <tr
                                                    onClick={() => setSelectedAssignment(selectedAssignment?.id === a.id ? null : a)}
                                                    aria-expanded={selectedAssignment?.id === a.id}
                                                    className={`border-b cursor-pointer ${rowCls} ${selectedAssignment?.id === a.id ? 'bg-[var(--surface-sunken)]' : ''}`}
                                                >
                                                    {/* BL-183: confirmation_number visible on every row */}
                                                    <ResponsiveCell label="Confirmation #" className={`px-4 py-3 font-mono font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                                                        {a.confirmation_number || '—'}
                                                    </ResponsiveCell>
                                                    <ResponsiveCell label="Attempt" className={`px-4 py-3 font-mono ${dark ? 'text-gray-300' : 'text-gray-600'}`}>#{a.attempt_sequence}</ResponsiveCell>
                                                    <ResponsiveCell label="Status" className="px-4 py-3"><StatusPill status={a.status} /></ResponsiveCell>
                                                    <ResponsiveCell label="Type" className={`px-4 py-3 capitalize ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{a.assignment_type || '—'}</ResponsiveCell>
                                                    <ResponsiveCell label="Driver" className={`px-4 py-3 text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {driverPrimaryLabel(assignedDriver, assignedDriverId)}
                                                    </ResponsiveCell>
                                                    <ResponsiveCell label="Created" className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}
                                                    </ResponsiveCell>
                                                </tr>
                                                {selectedAssignment?.id === a.id && (
                                                    <tr>
                                                        <td colSpan={6} className="px-4 pb-4 pt-1 bg-[var(--surface-canvas)]">
                                                            <AssignmentDetailCard
                                                                assignment={selectedAssignment}
                                                                drivers={drivers}
                                                                vehicles={vehicles}
                                                                onClose={() => setSelectedAssignment(null)}
                                                                onStatusChange={canEditDispatch ? handleStatusChange : null}
                                                                theme={theme}
                                                                userId={userId}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </ResponsiveTable>
                        )}
                    </div>
                </div>
            )}

            {/* ================================================================
                TAB 3: PAYABLE STAFF (W-2 internal drivers, Dispatch-full tier)
                ================================================================ */}
            {tab === 'staff' && (
                <div className="flex-1 overflow-auto">
                    {drivers.filter(d => d.employment_classification === 'w2_staff').length === 0 ? (
                        <div className="p-10 text-center">
                            <div className="text-4xl mb-2">👷</div>
                            <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No W-2 staff on roster</div>
                            <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Staff drivers linked in Personnel appear here at their current roster status. Only active drivers can receive assignments.</div>
                        </div>
                    ) : (
                        <div className={`rounded-xl border overflow-auto ${cardCls}`}>
                            <ResponsiveTable className="w-full text-sm">
                                <thead>
                                    <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                        <th className="px-4 py-3">Driver</th>
                                        <th className="px-4 py-3">Classification</th>
                                        <th className="px-4 py-3">Roster Status</th>
                                        <th className="px-4 py-3">Default Vehicle</th>
                                        <th className="px-4 py-3">Added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drivers
                                        .filter(d => d.employment_classification === 'w2_staff')
                                        .map(d => {
                                            const dv = vehicles.find(v => v.id === d.default_vehicle_id);
                                            return (
                                                <tr key={d.id} className={`border-b ${rowCls}`}>
                                                    <ResponsiveCell label="Driver" className={`px-4 py-3 ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                                                        <div className="text-sm font-medium">{driverPrimaryLabel(d)}</div>
                                                    </ResponsiveCell>
                                                    <ResponsiveCell label="Classification" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>W-2 Staff</ResponsiveCell>
                                                    <ResponsiveCell label="Roster Status" className="px-4 py-3">
                                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                            {d.status || '—'}
                                                        </span>
                                                        {!isUser && !d.has_app_login && <span className="block mt-1 text-xs text-amber-700">No app login yet</span>}
                                                        {!isUser && <ManualDriverActivation driver={d} onActivated={loadDriversAndVehicles} />}
                                                    </ResponsiveCell>
                                                    <ResponsiveCell label="Default Vehicle" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                                        {dv ? `${dv.label || dv.vehicle_class || '—'} ${dv.plate ? `(${dv.plate})` : ''}`.trim() : '—'}
                                                    </ResponsiveCell>
                                                    <ResponsiveCell label="Added" className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                                                    </ResponsiveCell>
                                                </tr>
                                            );
                                        })}
                                </tbody>
                            </ResponsiveTable>
                        </div>
                    )}
                </div>
            )}

            {tab === 'escalated' && (
                <div className={`flex-1 overflow-auto rounded-xl border ${cardCls}`}>
                    {escalationLoading ? (
                        <div className="p-10 text-center text-gray-500">Loading escalated intake…</div>
                    ) : escalationError ? (
                        <div className="p-6 text-center text-red-600">Could not load escalated intake: {escalationError}</div>
                    ) : escalations.length === 0 ? (
                        <div className="p-10 text-center">
                            <div className="text-4xl mb-2">🚨</div>
                            <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No escalated intake</div>
                            <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>AI Attendant handoffs will appear here.</div>
                        </div>
                    ) : (
                        <ResponsiveTable className="w-full text-sm">
                            <thead>
                                <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                    <th className="px-4 py-3">Channel</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">Tier</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Itinerary</th><th className="px-4 py-3">Passes</th><th className="px-4 py-3">Last Inbound</th>
                                </tr>
                            </thead>
                            <tbody>
                                {escalations.map((session) => {
                                    const account = accountsById.get(session.account_id);
                                    const client = account?.account_name || formatPhone(session.contact_phone) || session.contact_email || (session.account_id ? `${session.account_id.slice(0, 8)}…` : '—');
                                    return (
                                        <React.Fragment key={session.id}>
                                            <tr onClick={() => setSelectedEscalation(selectedEscalation?.id === session.id ? null : session)} className={`border-b cursor-pointer ${rowCls}`}>
<ResponsiveCell label="Channel" className={`px-4 py-3 capitalize ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{session.channel || '—'}</ResponsiveCell><ResponsiveCell label="Client" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{client}</ResponsiveCell>
                                                <ResponsiveCell label="Tier" className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${dark ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-600'}`}>{session.client_tier || '—'}</span></ResponsiveCell>
<ResponsiveCell label="Reason" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{session.escalation_reason || '—'}</ResponsiveCell><ResponsiveCell label="Itinerary" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{session.itinerary_short_form || '—'}</ResponsiveCell><ResponsiveCell label="Passes" className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>{session.pass_count} / {session.pass_cap}</ResponsiveCell>
                                                <ResponsiveCell label="Last Inbound" className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{session.last_inbound_at ? new Date(session.last_inbound_at).toLocaleString() : (session.created_at ? new Date(session.created_at).toLocaleString() : '—')}</ResponsiveCell>
                                            </tr>
                                            {selectedEscalation?.id === session.id && (
                                                <tr><td colSpan={7} className="px-4 pb-4 pt-1 bg-[var(--surface-canvas)]"><EscalatedIntakeDetailCard intakeSession={session} onClose={() => setSelectedEscalation(null)} onResolve={canEditDispatch ? handleEscalationResolve : null} resolving={resolvingEscalation} theme={theme} /></td></tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </ResponsiveTable>
                    )}
                </div>
            )}
        </div>
    );
}

export default DispatchBoardView;
